import React from 'react';
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';

const ICON_OPTIONS = [
  { value: 'shield', label: 'Shield' },
  { value: 'chart', label: 'Chart' },
  { value: 'globe', label: 'Globe' },
  { value: 'award', label: 'Award / Medal' },
  { value: 'headset', label: 'Headset' },
  { value: 'lock', label: 'Lock' },
  { value: 'check', label: 'Check' },
  { value: 'users', label: 'Users' },
  { value: 'target', label: 'Target' },
];

const emptyFeature = (index) => ({
  icon: 'shield',
  title: '',
  order: index,
});

const WhyChooseSectionEditor = ({ form, updateField }) => {
  const features = form.whyChooseFeatures || [];

  const updateFeature = (index, field, value) => {
    const next = [...features];
    next[index] = { ...next[index], [field]: value };
    updateField('whyChooseFeatures', next);
  };

  const addFeature = () => {
    updateField('whyChooseFeatures', [...features, emptyFeature(features.length)]);
  };

  const removeFeature = (index) => {
    if (!window.confirm('Remove this feature card?')) return;
    updateField(
      'whyChooseFeatures',
      features.filter((_, i) => i !== index).map((feature, i) => ({ ...feature, order: i }))
    );
  };

  const moveFeature = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= features.length) return;
    const next = [...features];
    [next[index], next[target]] = [next[target], next[index]];
    updateField('whyChooseFeatures', next.map((feature, i) => ({ ...feature, order: i })));
  };

  return (
    <div className="space-y-5 border-t border-gray-100 dark:border-dark-700 pt-5">
      <h4 className="text-sm font-bold text-gray-900 dark:text-white">Section Badge, Image Overlay & Feature Cards</h4>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Tag (above title)</label>
          <input
            className="input-modern"
            value={form.whyChooseTag || ''}
            onChange={(e) => updateField('whyChooseTag', e.target.value)}
            placeholder="Why USA"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Image Badge Label</label>
          <input
            className="input-modern"
            value={form.whyChooseImageBadgeLabel || ''}
            onChange={(e) => updateField('whyChooseImageBadgeLabel', e.target.value)}
            placeholder="Trusted by"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Image Badge Text</label>
          <input
            className="input-modern"
            value={form.whyChooseImageBadgeText || ''}
            onChange={(e) => updateField('whyChooseImageBadgeText', e.target.value)}
            placeholder="1000+ Entrepreneurs"
          />
        </div>
      </div>

      <div className="space-y-1.5 max-w-xs">
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Image Badge Icon</label>
        <select
          className="input-modern"
          value={form.whyChooseImageBadgeIcon || 'shield'}
          onChange={(e) => updateField('whyChooseImageBadgeIcon', e.target.value)}
        >
          {ICON_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Feature Cards (2x2 grid)</label>
        <button
          type="button"
          onClick={addFeature}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg"
        >
          <FaPlus className="w-3 h-3" /> Add Feature
        </button>
      </div>

      {features.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No feature cards. Click Add Feature or Reload Defaults.</p>
      ) : (
        <div className="space-y-3">
          {features.map((feature, index) => (
            <div key={feature._id || `feature-${index}`} className="rounded-xl border border-gray-200 dark:border-dark-600 p-4 bg-gray-50/50 dark:bg-dark-900/30">
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-xs font-bold text-gray-600 dark:text-gray-300">Feature {index + 1}</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => moveFeature(index, -1)} disabled={index === 0} className="p-1.5 rounded border border-gray-200 dark:border-dark-600 disabled:opacity-40">
                    <FaArrowUp className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={() => moveFeature(index, 1)} disabled={index === features.length - 1} className="p-1.5 rounded border border-gray-200 dark:border-dark-600 disabled:opacity-40">
                    <FaArrowDown className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={() => removeFeature(index)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                    <FaTrash className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold uppercase text-gray-500">Icon</label>
                  <select className="input-modern text-sm" value={feature.icon || 'shield'} onChange={(e) => updateFeature(index, 'icon', e.target.value)}>
                    {ICON_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold uppercase text-gray-500">Title</label>
                  <input className="input-modern text-sm" value={feature.title || ''} onChange={(e) => updateFeature(index, 'title', e.target.value)} placeholder="Strong Legal Protection" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhyChooseSectionEditor;
